/**
 * SessionInvalidError — typed error mapped from leadbilling's `AuthRole`
 * middleware response (HTTP 401).
 *
 * Aggregates all 401 sub-codes returned by the middleware:
 *   - `missing_authorization_header`
 *   - `malformed_authorization_header`
 *   - `invalid_token`
 *
 * From the user's perspective these all mean "your session is no longer
 * valid; please sign in again." Caught in `ConfirmPurchaseModal` to render
 * an informative toast (and optionally trigger a global `signOut()` flow).
 *
 * Aligns with the repo's `tClassifiedError` pattern (see
 * `NoPaymentMethodError`): an `Error` carrying a stable `code` discriminator
 * that consumers detect via `instanceof` (preferred) or duck-type on
 * `error.code`.
 */
export class SessionInvalidError extends Error {
  /** Discriminator — collapses all 401 sub-codes into one client-side bucket. */
  readonly code = 'session_invalid' as const;

  /**
   * Original backend code, preserved for telemetry/Sentry breadcrumbs without
   * affecting the consumer-visible discriminator.
   */
  readonly backendCode?: string;

  constructor(
    message = 'Session is missing, malformed, or expired.',
    backendCode?: string,
  ) {
    super(message);
    this.name = 'SessionInvalidError';
    this.backendCode = backendCode;
    // Restore prototype chain after extending built-in Error (TS target ES5+ safety).
    Object.setPrototypeOf(this, SessionInvalidError.prototype);
  }
}

/** Type-guard for non-instanceof contexts (e.g. structured-clone boundaries). */
export const isSessionInvalidError = (
  err: unknown,
): err is SessionInvalidError =>
  err instanceof SessionInvalidError ||
  (err instanceof Error &&
    'code' in err &&
    (err as { code?: unknown }).code === 'session_invalid');
